/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import * as React from 'react'
import { Formik, Form, FieldMetaProps } from 'formik'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import { IIncident, IIncidentEmail } from '../../../models/Incident'
import { logError } from '../../../logging'
import { FormikField } from '../../form_controls/FormikField'
import { FormControlLabel, Grid, Typography } from '@material-ui/core'
import { GridProps } from '@material-ui/core/Grid'
import { UserFacingError } from '../../../errors/UserFacingError'
import TextChipListControl from '../../form_controls/TextChipListControl'
import { PageLoader } from '../../common/PageLoader'
import TextControl from '../../form_controls/TextControl'
import TextAreaControl from '../../form_controls/TextAreaControl'
import CheckboxControl from '../../form_controls/CheckboxControl'
import {
  IOneplaceLibraryContextProp,
  withOneplaceLibraryContext
} from '../../OneplaceLibraryProvider'
import { IUserCapabilities } from '../../../models/User'
import { TemplateType } from '../../../models/Template'

export interface IIncidentEmailDialogProps extends IOneplaceLibraryContextProp {
  isOpen: boolean
  incident: IIncident
  capabilities: IUserCapabilities
  franchiseeId: number
  onClose: () => void
  onSubmitDialogsCompleted: () => void
  prePopulatedEmails?: string
  goToDashboard: () => void
  templateType?: TemplateType
}

export interface IIncidentEmailDialogState {
  stage:
    | 'loading'
    | 'edit_email'
    | 'sending'
    | 'sent'
    | 'error'
    | 'submit_error'
  initialValues: IIncidentEmail
  sendEnabled: boolean
  submitError: string
}

/* eslint-disable react/display-name */
export const IncidentEmailDialog = withOneplaceLibraryContext(
  class extends React.Component<
    IIncidentEmailDialogProps,
    IIncidentEmailDialogState
  > {
    messageContent: IIncidentEmail = null as any

    constructor(props: IIncidentEmailDialogProps) {
      super(props)
      this.onValidate = this.onValidate.bind(this)
      this.onSendClicked = this.onSendClicked.bind(this)
      this.onClose = this.onClose.bind(this)
      this.onSubmitErrorClose = this.onSubmitErrorClose.bind(this)

      this.state = {
        stage: 'loading',
        initialValues: {
          emailEditorData: null as any,
          sendPDF: false,
          sendURL: false,
          invalidateURL: false
        },
        sendEnabled: true,
        submitError: ''
      }
      void this.loadTemplate()
    }

    async loadTemplate() {
      const incident = this.props.incident
      const capabilities = this.props.capabilities
      try {
        const template = await this.props.ctx.client.getIncidentEmail(
          incident.id ? incident.id : 0,
          capabilities.franchiseId,
          this.props.franchiseeId,
          this.props.templateType
        )

        this.messageContent = {
          emailEditorData: {
            emailAdd: template.message.toEmail,
            ccEmailAdd: template.message.ccEmail,
            emailTitle: template.message.subject,
            emailContent: template.message.message
          },
          sendPDF: false,
          sendURL: false,
          invalidateURL: false
        }

        if (
          template.message.toEmail &&
          incident.toEmail &&
          !incident.editEmail
        ) {
          this.messageContent = {
            ...this.messageContent,
            sendPDF: true
          }
          try {
            await this.props.ctx.client.sendIncidentEmail(
              incident.id ? incident.id : 0,
              this.messageContent,
              capabilities.franchiseId,
              this.props.franchiseeId
            )
            this.setState({
              stage: 'sent'
            })
          } catch (e) {
            if (e instanceof UserFacingError) {
              this.onValidate(this.messageContent)
              this.setState({
                stage: 'submit_error',
                submitError: e.message,
                initialValues: this.messageContent
              })
            } else {
              logError(e, 'Error while loading incident email.')
              this.setState({
                stage: 'error'
              })
            }
          }
        } else {
          if (
            this.props.prePopulatedEmails &&
            this.props.prePopulatedEmails.trim().length > 0
          ) {
            if (!template.message.toEmail) template.message.toEmail = ''
            template.message.toEmail += `,${this.props.prePopulatedEmails}`
            this.messageContent = {
              ...this.messageContent,
              emailEditorData: {
                ...this.messageContent.emailEditorData,
                emailAdd: template.message.toEmail
              }
            }
          }
          this.setState({
            stage: 'edit_email',
            initialValues: this.messageContent
          })
          this.onValidate(this.messageContent)
        }
      } catch (e) {
        if (e instanceof UserFacingError) {
          this.setState({
            stage: 'submit_error',
            submitError: e.message
          })
        } else {
          logError(e, 'Error while loading incident email.')
          this.setState({
            stage: 'error'
          })
        }
      }
    }

    onValidate(fields: IIncidentEmail) {
      this.messageContent = fields
      const valid = Boolean(
        fields.emailEditorData.emailAdd &&
          fields.emailEditorData.emailAdd.trim() &&
          fields.emailEditorData.emailTitle.trim() &&
          fields.emailEditorData.emailContent.trim()
      )
      if (valid != this.state.sendEnabled) {
        this.setState({ sendEnabled: valid })
      }
    }

    onClose() {
      this.props.onClose()
      this.props.onSubmitDialogsCompleted()
    }

    onSubmitErrorClose() {
      this.setState({ stage: 'edit_email' })
    }

    async onSendClicked() {
      this.messageContent.sendPDF = this.state.initialValues.sendPDF
      this.messageContent.sendURL = this.state.initialValues.sendURL
      this.messageContent.invalidateURL = this.state.initialValues.invalidateURL

      this.setState({
        stage: 'sending'
      })
      try {
        await this.props.ctx.client.sendIncidentEmail(
          this.props.incident.id ? this.props.incident.id : 0,
          this.messageContent,
          this.props.capabilities.franchiseId,
          this.props.franchiseeId
        )
        this.setState({
          stage: 'sent'
        })
      } catch (e) {
        if (e instanceof UserFacingError) {
          this.setState({
            stage: 'submit_error',
            submitError: e.message,
            initialValues: this.messageContent
          })
        } else {
          logError(e, 'Error while loading incident email.')
          this.setState({
            stage: 'error'
          })
        }
      }
    }

    getMetaInfo() {
      return {
        value: null,
        touched: false,
        initialTouched: false
      } as FieldMetaProps<any>
    }

    setEmailOptions = (option: string): void => {
      switch (option) {
        case 'sendPDF':
          this.setState({
            initialValues: {
              ...this.state.initialValues,
              sendPDF: !this.state.initialValues.sendPDF
            }
          })
          break
        case 'sendURL':
          this.setState({
            initialValues: {
              ...this.state.initialValues,
              sendURL: !this.state.initialValues.sendURL
            }
          })
          break
        case 'invalidateURL':
          this.setState({
            initialValues: {
              ...this.state.initialValues,
              invalidateURL: !this.state.initialValues.invalidateURL
            }
          })
          break
      }
    }

    isEmailOptionValid = (): boolean => {
      return (
        this.state.initialValues.sendPDF || this.state.initialValues.sendURL
      )
    }

    render() {
      const t = this.props.ctx.i18next.t
      const gridProps: GridProps = { xs: 12 }

      return (
        <Dialog
          open={this.props.isOpen}
          PaperProps={{ style: { width: '95%', maxWidth: 700 } }}
          disableBackdropClick={true}
        >
          <DialogTitle>{t('email_incident')}</DialogTitle>
          <DialogContent>
            {(this.state.stage == 'loading' ||
              this.state.stage == 'sending') && (
              <PageLoader loading={true} paddingTop={0} />
            )}
            {this.state.stage == 'edit_email' && (
              <>
                <Formik
                  initialValues={this.state.initialValues}
                  onSubmit={(_values, _actions) => {
                    return
                  }}
                  validate={this.onValidate}
                >
                  {() => (
                    <Form>
                      <Grid container spacing={2}>
                        <FormikField
                          name='emailEditorData.emailAdd'
                          label={t('email_to')}
                          component={TextChipListControl}
                          gridProps={gridProps}
                        />
                        <FormikField
                          name='emailEditorData.ccEmailAdd'
                          label={t('email_cc')}
                          component={TextChipListControl}
                          gridProps={gridProps}
                        />
                        <FormikField
                          name='emailEditorData.emailTitle'
                          label={t('email_subject')}
                          component={TextControl}
                          gridProps={gridProps}
                        />
                        <FormikField
                          name='emailEditorData.emailContent'
                          label={t('email_message')}
                          component={TextAreaControl}
                          gridProps={gridProps}
                        />
                      </Grid>
                    </Form>
                  )}
                </Formik>

                <div style={{ marginTop: '25px' }}>
                  <Typography variant='subtitle1' display='inline'>
                    {t('incident_email_include')}
                  </Typography>
                  <Typography variant='caption' display='inline'>
                    {t('incident_email_include_tip')}
                  </Typography>

                  <div>
                    <FormControlLabel
                      control={
                        <CheckboxControl
                          id='includePDF'
                          field={
                            {
                              name: 'includePDF',
                              value: this.state.initialValues.sendPDF
                            } as any
                          }
                          form={
                            {
                              setFieldValue: () =>
                                this.setEmailOptions('sendPDF')
                            } as any
                          }
                          meta={this.getMetaInfo()}
                        />
                      }
                      label={t('incident_email_include_pdf')}
                    />
                  </div>
                  <div>
                    <FormControlLabel
                      control={
                        <CheckboxControl
                          id='includeLink'
                          field={
                            {
                              name: 'includeLink',
                              value: this.state.initialValues.sendURL
                            } as any
                          }
                          form={
                            {
                              setFieldValue: () =>
                                this.setEmailOptions('sendURL')
                            } as any
                          }
                          meta={this.getMetaInfo()}
                        />
                      }
                      label={t('incident_email_include_link')}
                    />
                  </div>

                  {this.props.incident.path &&
                    this.state.initialValues.sendURL && (
                      <div style={{ marginLeft: '32px' }}>
                        <FormControlLabel
                          control={
                            <CheckboxControl
                              id='replaceLink'
                              field={
                                {
                                  name: 'replaceLink',
                                  value: this.state.initialValues.invalidateURL
                                } as any
                              }
                              form={
                                {
                                  setFieldValue: () =>
                                    this.setEmailOptions('invalidateURL')
                                } as any
                              }
                              meta={this.getMetaInfo()}
                            />
                          }
                          label={t('incident_toemail_invalidate')}
                        />
                      </div>
                    )}
                </div>
              </>
            )}
            {this.state.stage == 'error' && (
              <PageLoader
                loading={false}
                paddingTop={0}
                status={t('email_process_error')}
              />
            )}
            {this.state.stage == 'sent' && (
              <PageLoader
                loading={false}
                paddingTop={0}
                status={t('email_sent_successfully')}
              />
            )}
            {this.state.stage == 'submit_error' && (
              <PageLoader
                loading={false}
                paddingTop={0}
                status={this.state.submitError}
              />
            )}
          </DialogContent>
          <DialogActions>
            {this.state.stage == 'edit_email' && (
              <>
                <Button onClick={this.onClose} color='primary'>
                  {t('cancel')}
                </Button>
                <Button
                  disabled={
                    !this.state.sendEnabled || !this.isEmailOptionValid()
                  }
                  onClick={this.onSendClicked}
                  color='primary'
                >
                  {t('send_email')}
                </Button>
              </>
            )}
            {this.state.stage == 'error' && (
              <>
                <Button onClick={this.onClose} color='primary'>
                  {t('close')}
                </Button>
              </>
            )}
            {this.state.stage == 'submit_error' && (
              <>
                <Button onClick={this.onSubmitErrorClose} color='primary'>
                  {t('close')}
                </Button>
              </>
            )}
            {this.state.stage == 'sent' && (
              <>
                <Button onClick={this.onClose} color='primary'>
                  {t('continue_editing')}
                </Button>
                <Button onClick={this.props.goToDashboard} color='primary'>
                  {t('go_to_dashboard')}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )
    }
  }
)
